import React, { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { List, Plus, Menu, Loader2, LogIn, Download, Upload } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useData } from "./components/DataContext";
import { useAuth } from "./lib/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import ProfileImportDialog from "./components/profile/ProfileImportDialog";
import { exportProfile, downloadProfile } from "@/utils/profile";

const navigationItems = [
  {
    title: "All Lists",
    url: createPageUrl("Dashboard"),
    icon: List,
  },
  {
    title: "New List",
    url: createPageUrl("CreateList"),
    icon: Plus,
  },
];

interface AppLayoutProps {
  children: ReactNode;
  currentPageName?: string;
}

function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const { isUpdating, user, userLoading } = useData();
  const { logout } = useAuth();
  const { toast } = useToast();

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.reload();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const profile = await exportProfile();
      downloadProfile(profile);
      toast({
        title: "Profile Exported",
        description: "Your profile JSON has been downloaded successfully.",
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: err.message || "Failed to export profile.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-slate-50">
        <Sidebar className="border-r border-slate-200 bg-white">
          <SidebarHeader className="border-b border-slate-100 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <List className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900 text-lg">Lists</h2>
                  <p className="text-xs text-slate-500">Material Design</p>
                </div>
              </div>
              <AnimatePresence>
                {isUpdating && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                  >
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="p-3">
            {/* User Section */}
            <div className="px-3 py-4 border-b border-slate-100 mb-3">
              {userLoading ? (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-200 rounded-full animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded animate-pulse mb-1" />
                    <div className="h-3 bg-slate-200 rounded animate-pulse w-2/3" />
                  </div>
                </div>
              ) : user ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.full_name || user.email}
                        className="w-8 h-8 rounded-full object-cover border border-slate-200"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                        {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {user.full_name || user.email}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="w-full border-slate-200 hover:bg-slate-50"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              )}
            </div>

            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2">
                Navigation
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-xl mb-1 ${
                          location.pathname === item.url ? 'bg-blue-50 text-blue-700 font-medium' : ''
                        }`}
                      >
                        <Link to={item.url} className="flex items-center gap-3 px-4 py-3">
                          <item.icon className="w-5 h-5" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Profile Import / Export */}
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2">
                Backup & Restore
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleExport}
                      disabled={isExporting || !user}
                      className="hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-xl mb-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 px-4 py-3 w-full">
                        {isExporting ? <Loader2 className="w-5 h-5 animate-spin text-blue-500" /> : <Download className="w-5 h-5" />}
                        <span>Export Profile</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => setShowImportDialog(true)}
                      disabled={!user}
                      className="hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-xl mb-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 px-4 py-3 w-full">
                        <Upload className="w-5 h-5" />
                        <span>Import Profile</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <main className="flex-1 flex flex-col bg-slate-50">
          <header className="bg-white border-b border-slate-200 px-6 py-4 md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200">
                  <Menu className="w-5 h-5" />
                </SidebarTrigger>
                <h1 className="text-xl font-semibold text-slate-900">Lists</h1>
              </div>
              <AnimatePresence>
                {isUpdating && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            {children}
          </div>
        </main>
      </div>

      <ProfileImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
      />
    </SidebarProvider>
  );
}

export default function LayoutWrapper({ children, currentPageName }: AppLayoutProps) {
  return (
    <AppLayout currentPageName={currentPageName}>
      {children}
    </AppLayout>
  );
}
